import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Empty, Form, Popover } from 'antd';
import moment from 'moment';
import { Card } from '../../Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useFormSubmissionData } from '@pages/AppUsers/AppUserFormDetail';
import IconButton from '@components/IconButton';
import { fetchForms } from '@redux/forms/actions';
import { fetchAppUsersForms } from '@redux/appUsers/actions';
import { selectAppUserFormComputations } from '@redux/appUsers/reducers';
import useTranslatableData from '@hooks/useTranslatableData';
import './style.less';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 10 },
    md: { span: 8 },
  },
  layout: 'vertical',
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 14 },
    md: { span: 16 },
  },
};

function formatComputedValue(value) {
  if (typeof value === 'number') {
    const result = value.toFixed(1);
    return Number(result) === -0 ? 0 : result;
  }

  return value;
}

const INCREASE = 'increase';
const DECREASE = 'decrease';

function Arrow({ improvement, previous, value }) {
  const [className, setClassName] = useState('');
  const [icon, setIcon] = useState();

  useEffect(() => {
    if (previous !== undefined) {
      if (
        (value < previous && improvement === DECREASE) ||
        (value > previous && improvement === INCREASE)
      ) {
        setClassName('green-arrow');
      } else if (
        (value > previous && improvement === DECREASE) ||
        (value < previous && improvement === INCREASE)
      ) {
        setClassName('red-arrow');
      }

      if (value < previous) setIcon(faArrowDown);
      if (value > previous) setIcon(faArrowUp);
    }
  }, [improvement, previous, value]);

  return (
    <div className={className}>
      {icon ? <FontAwesomeIcon icon={icon} size="1.5x" /> : null}
      {formatComputedValue(value)}
    </div>
  );
}

function FormScoreCard({
  displayNames,
  improvements,
  scoreData,
  loading,
  canDownload,
  canPreview,
  ordering,
  formVersion,
  ...props
}) {
  const { t } = useTranslation();
  const translate = useTranslatableData();

  const computationCutpoints = {};
  if (formVersion) {
    Object.keys(formVersion.computations).forEach(computationId => {
      computationCutpoints[computationId] = {};
      const computation = formVersion.computations[computationId];
      if (computation.cutpoints) {
        computation.cutpoints.forEach(cutpoint => {
          computationCutpoints[computationId][cutpoint.id] = cutpoint;
        });
      }
    });
  }

  return (
    <Card.Half
      className="form-scores-card"
      noPadding
      title={t('cards:FormScores.title')}
      loading={loading}
      // extra={
      //   <IconButton
      //     icon={faInfoCircle}
      //     tooltip={t('common:buttons.delete')}
      //     // onClick={onDelete}
      //   />
      // }
      {...props}
    >
      {!scoreData ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <div>
          <div className="scores-container">
            <div>
              {ordering.map(({ key }) =>
                key === '_created' ||
                (scoreData.previous[key] === null && scoreData.latest[key] === null) ? null : (
                  <span key={key}>
                    <span className="form-score-name">{translate(displayNames[key])}</span>
                    {formVersion.computations[scoreData.latest._metadata[key].using || key]
                      .description || // form schema has a description for this computation
                    formVersion.computations[scoreData.latest._metadata[key].using || key]
                      .trajectory || // form schema has info re. trajectory for this computation
                    formVersion.computations[scoreData.latest._metadata[key].using || key]
                      .meaningful_change || // form schema has info re. meaningful change for this computation
                    formVersion.computations[scoreData.latest._metadata[key].using || key]
                      .information || // form schema has additional information for this computation
                    (scoreData.latest._cutpoints && scoreData.latest._cutpoints[key]) ? ( // current submission has a cutpoint for this computation
                      <Popover
                        title={<strong>{translate(displayNames[key])}</strong>}
                        overlayClassName="form-scores-popover"
                        content={
                          <>
                            {formVersion.computations[scoreData.latest._metadata[key].using || key]
                              .description ? (
                              <p>
                                <strong>{t('cards:FormScores.descriptionLabel')}:</strong>{' '}
                                {translate(
                                  formVersion.computations[
                                    scoreData.latest._metadata[key].using || key
                                  ].description,
                                )}
                              </p>
                            ) : null}
                            {formVersion.computations[scoreData.latest._metadata[key].using || key]
                              .trajectory ? (
                              <p>
                                <strong>{t('cards:FormScores.trajectoryLabel')}:</strong>{' '}
                                {translate(
                                  formVersion.computations[
                                    scoreData.latest._metadata[key].using || key
                                  ].trajectory,
                                )}
                              </p>
                            ) : null}
                            {formVersion.computations[scoreData.latest._metadata[key].using || key]
                              .meaningful_change ? (
                              <p>
                                <strong>{t('cards:FormScores.meaningfulChangeLabel')}:</strong>{' '}
                                {translate(
                                  formVersion.computations[
                                    scoreData.latest._metadata[key].using || key
                                  ].meaningful_change,
                                )}
                              </p>
                            ) : null}
                            {scoreData.latest._cutpoints &&
                            scoreData.latest._cutpoints[key] &&
                            formVersion.computations[scoreData.latest._metadata[key].using || key]
                              .cutpoints ? (
                              <p>
                                <strong>{t('cards:FormScores.cutpointsLabel')}:</strong>{' '}
                                <ul>
                                  {formVersion.computations[
                                    scoreData.latest._metadata[key].using || key
                                  ].cutpoints.map(cutpoint => (
                                    <li key={cutpoint.id}>
                                      {translate(cutpoint.display_name)}: {cutpoint.operator}{' '}
                                      {cutpoint.value}
                                    </li>
                                  ))}
                                </ul>
                              </p>
                            ) : null}
                            {formVersion.computations[
                              scoreData.latest._metadata[key].using || key
                            ].information?.map((info, index) => (
                              <p key={index}>
                                <strong>{translate(info.name)}:</strong> {translate(info.value)}
                              </p>
                            ))}
                          </>
                        }
                      >
                        <IconButton icon={faInfoCircle} />
                      </Popover>
                    ) : null}
                    <Arrow
                      improvement={improvements[key]}
                      previous={scoreData.previous[key] || undefined}
                      value={scoreData.latest[key] || undefined}
                    />
                    {scoreData.latest._cutpoints && scoreData.latest._cutpoints[key]
                      ? translate(
                          computationCutpoints[scoreData.latest._metadata[key].using]?.[
                            scoreData.latest._cutpoints[key]?.value
                          ]?.display_name || '',
                        )
                      : null}
                  </span>
                ),
              )}
            </div>
          </div>
          <Form {...formItemLayout} className="details-form submission-dates">
            <Form.Item label={t('cards:FormScores.labels.submitted')}>
              <span className="ant-form-text">
                {moment(scoreData.latest._created).format('L LT')}
              </span>
            </Form.Item>
            {scoreData.previous._created ? (
              <Form.Item label={t('cards:FormScores.labels.previousSubmission')}>
                <span className="ant-form-text">
                  {moment(scoreData.previous._created).format('L LT')}
                </span>
              </Form.Item>
            ) : null}
          </Form>
        </div>
      )}
    </Card.Half>
  );
}

const FormScoreCardbyId = ({ appUserId, formId, ...props }) => {
  const dispatch = useDispatch();
  const {
    displayNames,
    improvements,
    objectVersion,
    ordering,
    formVersion,
  } = useFormSubmissionData();

  useEffect(() => {
    dispatch(fetchForms());
    dispatch(fetchAppUsersForms(appUserId));
  }, [dispatch, appUserId]);

  const [loading, computations] = useSelector(
    selectAppUserFormComputations(appUserId, formId, objectVersion),
  );

  const scoreData = useMemo(() => {
    if (computations) {
      return computations.find(computation => computation.dataObjectVersion === objectVersion);
    }
  }, [computations, objectVersion]);

  if (!scoreData) return null;

  return (
    <FormScoreCard
      scoreData={scoreData}
      displayNames={displayNames}
      improvements={improvements}
      loading={loading}
      ordering={ordering}
      formVersion={formVersion}
      {...props}
    />
  );
};

export { FormScoreCard };

export default FormScoreCardbyId;
